/**
 * Expression-internal vocabulary lexicon (foreign surface → English keyword).
 *
 * LokaScript translates COMMAND and ROLE-MARKER vocabulary via the pattern
 * matchers and `joinTokenText` normalization, but the LEAVES of an expression —
 * possessive property names, comparison/existence operators, connectives — are
 * captured verbatim from the source language. When the parse is rendered (or
 * executed) as English, those leaves leak the foreign surface: `put my value`
 * authored as es `poner mi valor` rendered `put my valor` (invalid English) and
 * would read a non-existent `.valor` DOM property at runtime.
 *
 * This module is the reverse lexicon the expression layer consults to normalize
 * those leaves to English, so the rendered/executed expression is canonical. It
 * fixes both production paths at once (foreign→English transpile AND
 * foreign→AST→execute), which is why the normalization happens at PARSE time
 * (see the foreign→English canonical-validity burndown,
 * docs-internal/EXPRESSION_INTERNAL_TRANSLATION_SCOPE.md).
 *
 * Covers possessive property NAMES, expression CONNECTIVES, and the positional
 * LOCATIVE, plus `joinExpressionTokens` — the single seam every raw-expression
 * capture joins through. Surfaces come from the per-language i18n dictionaries
 * (`packages/i18n/src/dictionaries/{code}.ts`) — semantic is upstream of i18n in
 * the build order, so the subset is copied here by
 * `packages/i18n/scripts/extract-property-lexicon.ts`. Regenerate with that
 * script if the dictionaries gain translations. Identity surfaces need no entry:
 * an unlisted one passes through unchanged.
 *
 * The invariant to preserve when extending: translation is ANCHORED to a slot,
 * never applied to a free token, unless the lexicon has vouched for the surface
 * as unambiguous. An expression is mostly code — user variables, string literals,
 * selectors — and a wrong rewrite here is silent.
 */

import type { ActionType, LanguagePattern, LanguageToken } from '../../types';
import {
  BINARY_OPERATORS,
  COMPARISON_OPERATORS,
  OPERATOR_CHARACTERS,
  RUN_OPERATORS,
} from './operators';
import type { LanguageProfile } from '../../generators/profiles/types';
import { commandSchemas } from '../../generators/command-schemas';
import { isValidReference } from '../../types';
import { ROLE_MARKER_CONCEPTS } from './marker-resolution';
import { endWordPlace, isEndWord, type EndWordPlace } from '../end-keywords';
import { getPatternsForLanguage, tryGetProfile } from '../../registry';
import { getEnglishPossessiveAdjective, getPossessiveReference } from './possessive-keywords';
import { NATIVE_COUNT_WORDS } from '../../patterns/count-words';
import { EVENT_NAMES } from './event-names';

// prettier-ignore
export const PROPERTY_NAME_LEXICON: Record<string, Record<string, string>> = {
  ar: { "قيمة": "value" },
  bn: { "অক্ষম": "disabled", "আইডি": "id", "চেক করা": "checked", "দৈর্ঘ্য": "length", "মান": "value" },
  de: { "wert": "value" },
  es: { "valor": "value" },
  fr: { "valeur": "value" },
  hi: { "अक्षम": "disabled", "चेक": "checked", "छिपा": "hidden", "मान": "value" },
  id: { "nilai": "value" },
  it: { "valore": "value" },
  ja: { "値": "value" },
  ko: { "값": "value" },
  ms: { "dilumpuhkan": "disabled", "ditanda": "checked", "nilai": "value", "panjang": "length" },
  pl: { "wartość": "value" },
  pt: { "valor": "value" },
  qu: { "chanin": "value" },
  ru: { "значение": "value", "отключено": "disabled", "отмечено": "checked", "скрыто": "hidden" },
  sw: { "thamani": "value" },
  th: { "ความยาว": "length", "ค่า": "value", "ปิดใช้งาน": "disabled", "เลือกแล้ว": "checked", "ไอดี": "id" },
  tl: { "haba": "length", "halaga": "value", "hindi_pinagana": "disabled", "naka_tsek": "checked" },
  tr: { "değer": "value" },
  uk: { "вимкнено": "disabled", "значення": "value", "позначено": "checked", "приховано": "hidden" },
  vi: { "ẩn": "hidden", "được chọn": "checked", "giá trị": "value", "vô hiệu": "disabled" },
  zh: { "值": "value" },
};

/**
 * Expression connectives (foreign surface → English), generated by the same
 * script as the property lexicon.
 *
 * Unlike a property name, a connective has no slot to vouch for it — it is
 * translated as a free token — so only surfaces the language does NOT reuse for
 * another sense are carried here. The generator's ambiguity guard drops the
 * collisions: th `เป็น` is both `as` and the copula `is` (translating it would
 * rewrite every Thai `is` condition), and `of` is omitted entirely because most
 * languages spell it the same as `from` (es `de`). `of` is emitted structurally
 * by the of-possessive anchor in {@link joinExpressionTokens} instead.
 *
 * The logical words a condition joins (`or`, `not`, `and`) are carried the same
 * way. The renderer writes them in the language, and where the tokenizer reads
 * the word back as a bare identifier the condition kept it: de `p oder q`
 * compared p with a variable named `oder`, and `nicht p` tested one named
 * `nicht`. Entries for words the tokenizer already normalizes (es `o`, pl `i`)
 * change nothing.
 *
 * So are the comparison words a value or a condition joins (`contains`,
 * `includes`, `equals`). The renderer writes them from semantic's lexicons (es
 * `contiene`), and no profile keyword read them back: `if [1, 2] contains 3`
 * was the truthy `[1, 2]`. (`matches` has a profile keyword, or stays English.)
 *
 * A word the tokenizer would split needs a whole-token EXTRAS entry for its
 * entry here to be read: hi `के_रूप_में` and `में_है` (at the underscore), qu
 * `ukupi_kan`, zh `等于` (`于` alone is `destination`) and pl `równa się`.
 *
 * ja 含む, ko 포함 and zh 包含 spell both `contains` and pick's range mode
 * `inclusive`, and each tokenizer normalizes the word to `inclusive`, which is
 * what the pick range reads. Inside a value it can only be `contains`, and this
 * table is read before a keyword's normalized form (expressionWordOf), so their
 * entries here decide it there.
 */
// prettier-ignore
export const CONNECTIVE_LEXICON: Record<string, Record<string, string>> = {
  ar: { "أو": "or", "كـ": "as", "ليس": "not", "و": "and", "يحتوي": "contains", "يساوي": "equals", "يشمل": "includes" },
  bn: { "অথবা": "or", "এবং": "and", "না": "not", "হিসাবে": "as" },
  de: { "als": "as", "beinhaltet": "includes", "enthält": "contains", "gleicht": "equals", "nicht": "not", "oder": "or", "und": "and" },
  es: { "como": "as", "contiene": "contains", "iguala": "equals", "incluye": "includes", "no": "not", "o": "or", "y": "and" },
  fr: { "comme": "as", "contient": "contains", "égale": "equals", "et": "and", "inclut": "includes", "non": "not", "ou": "or" },
  he: { "או": "or", "וגם": "and", "כ": "as", "לא": "not" },
  hi: { "और": "and", "के_रूप_में": "as", "नहीं": "not", "बराबर": "equals", "में_है": "includes", "या": "or", "शामिल": "contains" },
  id: { "atau": "or", "berisi": "contains", "bukan": "not", "dan": "and", "sama": "equals", "sebagai": "as", "termasuk": "includes" },
  it: { "come": "as", "contiene": "contains", "e": "and", "include": "includes", "non": "not", "o": "or", "uguale": "equals" },
  ja: { "そして": "and", "ではない": "not", "として": "as", "または": "or", "含む": "contains", "含める": "includes", "等しい": "equals" },
  ko: { "같다": "equals", "그리고": "and", "또는": "or", "로": "as", "아니": "not", "포함": "contains", "포함하다": "includes" },
  ms: { "atau": "or", "bukan": "not", "dan": "and", "mengandungi": "contains", "sama": "equals", "sebagai": "as", "termasuk": "includes" },
  pl: { "i": "and", "jako": "as", "lub": "or", "nie": "not", "obejmuje": "includes", "równa się": "equals", "zawiera": "contains" },
  pt: { "como": "as", "contém": "contains", "e": "and", "igual": "equals", "inclui": "includes", "não": "not", "ou": "or" },
  qu: { "churasqa": "includes", "hina": "as", "hinallataq": "and", "kikin": "equals", "ukupi_kan": "contains", "utaq": "or" },
  ru: { "включает": "includes", "и": "and", "или": "or", "как": "as", "не": "not", "равно": "equals", "содержит": "contains" },
  sw: { "au": "or", "ina": "contains", "kuwa": "as", "pamoja": "includes", "sawa": "equals", "si": "not" },
  th: { "ไม่": "not", "และ": "and", "หรือ": "or" },
  tl: { "at": "and", "bilang": "as", "hindi": "not", "katumbas": "equals", "naglalaman": "contains", "o": "or" },
  tr: { "değil": "not", "eşittir": "equals", "içerir": "contains", "olarak": "as", "ve": "and", "veya": "or" },
  uk: { "або": "or", "включає": "includes", "дорівнює": "equals", "і": "and", "містить": "contains", "не": "not", "як": "as" },
  vi: { "bằng": "equals", "chứa": "contains", "hoặc": "or", "không": "not", "như": "as", "và": "and" },
  zh: { "作为": "as", "包含": "contains", "包括": "includes", "和": "and", "或": "or", "等于": "equals", "非": "not" },
};

/**
 * Markers that introduce the owner in a prepositional ("of") possessive, for
 * languages where the marker does NOT tokenize to a `source`/`of` normalized
 * form. These are genitive linkers / particles that surface as bare identifiers
 * or particles (ms `daripada`, sw `ya`, vi `của`, zh `的`), mirroring the i18n
 * dict `of` modifier.
 *
 * Several normalize to OTHER senses (it `di`→tell, pl `z`/uk `з`→style,
 * qu `pa`/tr `nin`→destination), which is exactly why a normalized-form check
 * alone cannot find them. en `of` / ar `من` / ru `из` (→ `source`) and tl `ng`
 * are handled by the literal/normalized check in {@link isOfPossessiveMarker}.
 */
const OF_POSSESSIVE_MARKERS: Record<string, ReadonlySet<string>> = {
  ms: new Set(['daripada']),
  sw: new Set(['ya']),
  vi: new Set(['của']),
  zh: new Set(['的']),
  it: new Set(['di']),
  pl: new Set(['z']),
  uk: new Set(['з']),
  th: new Set(['ของ']),
  // Arabic genitive connector, laam + tatweel. Distinct from the possessive
  // pronoun لي (laam + ya), so listing it here cannot steal that fold.
  ar: new Set(['لـ']),
  ja: new Set(['の']),
  ko: new Set(['의']),
  bn: new Set(['র']),
  hi: new Set(['का']),
  qu: new Set(['pa']),
  // Genitive suffix with 4-way vowel harmony, buffered `n` after a vowel; the
  // profile renders `ın` for every owner, so all eight forms must read back.
  tr: new Set(['nin', 'nın', 'nun', 'nün', 'in', 'ın', 'un', 'ün']),
};

/**
 * Whether a token introduces the owner of a prepositional possessive
 * (`value of #price`, `valor de #picker`). Kept narrow, and only consulted when
 * a property head sits before it and a selector after, so it never shadows a
 * real source role.
 */
/**
 * The of-marker to EMIT when rendering a prepositional possessive.
 *
 * The renderer previously built this construction from
 * `profile.possessive.marker`, which disagrees with what the parser accepts in
 * two ways at once. That table is EMPTY for de/ar/id/pl/ru/sw/uk/ms, so those
 * languages skipped the marker switch entirely and fell through to the English
 * `'s` default — which is why corpus rows read `#picker's wartość`. And where it
 * was non-empty the operands were emitted object-first, while the parser's
 * of-possessive matcher wants the property first.
 *
 * Reading {@link OF_POSSESSIVE_MARKERS} here is the point: its own contract is
 * that "the possessive matchers and the raw-expression join cannot disagree
 * about what an of-marker is", and the renderer is a third party to that
 * agreement. Languages absent from the table are the ones whose marker
 * tokenizes to a `source` normalized form (es `de`, de `von`, fr/pt `de`, id
 * `dari`, ru `из`), so their source marker IS the of-marker.
 */
export function getOfPossessiveMarker(profile: LanguageProfile | undefined): string | undefined {
  if (!profile?.code) return undefined;
  const table = OF_POSSESSIVE_MARKERS[profile.code];
  if (table) {
    const [first] = table;
    if (first) return first;
  }
  return profile.roleMarkers?.source?.primary;
}

export function isOfPossessiveMarker(
  profile: LanguageProfile | undefined,
  token: LanguageToken
): boolean {
  const value = token.value.toLowerCase();
  const normalized = (token.normalized ?? '').toLowerCase();
  if (value === 'of' || value === 'ng' || normalized === 'of' || normalized === 'source') {
    return true;
  }
  const langMarkers = profile?.code ? OF_POSSESSIVE_MARKERS[profile.code] : undefined;
  return !!langMarkers && langMarkers.has(value);
}

/**
 * Locative markers inside a positional expression, by the role CONCEPT the
 * tokenizer normalizes them to.
 *
 * Role markers are registered with `normalized: <role name>`, which is right for
 * matching a role but wrong inside an expression: the raw text is read as
 * English, so es `en` (registered as the `destination` marker) emitted the role
 * name and rendered `scroll to last <.message/> destination #chat`. English's own
 * marker never hits this — `in` is not one of its role markers, so it passes
 * through — which is why only non-English languages showed the leak.
 *
 * The slot supplies the sense the role name cannot: a positional source clause is
 * locative (`last <.message/> in #chat`), so `destination` reads `in` and
 * `source` reads `from`. Mapping to the en profile's own `destination` marker
 * instead would wrongly yield `on` (its primary).
 */
const ROLE_CONCEPT_TO_ENGLISH_LOCATIVE: Record<string, string> = {
  destination: 'in',
  source: 'from',
};

/**
 * Every surface a language uses for a "within" preposition, generated from the
 * i18n dict's `in`/`into`/`at`/`within`/`inside` modifiers.
 *
 * These need NO ambiguity guard, unlike {@link CONNECTIVE_LEXICON}: they are read
 * ONLY in the positional expression's locative slot, and the slot fixes the
 * sense. es spelling `in`/`into`/`at` all as `en` is therefore harmless here —
 * in this position every one of them means `in`.
 *
 * Needed because these markers do not all normalize the same way: zh `在`,
 * tr `içinde` and id `dalam` are not role markers at all and leaked verbatim,
 * while pt `dentro` normalizes to `into` — which the canonical parser rejects in
 * this slot.
 */
// prettier-ignore
const LOCATIVE_SURFACES: Record<string, ReadonlySet<string>> = {
  ar: new Set(["عند", "في"]),
  bn: new Set(["এ", "তে"]),
  de: new Set(["bei"]),
  es: new Set(["en"]),
  fr: new Set(["dans", "en", "à"]),
  he: new Set(["ב", "בתוך", "לתוך"]),
  hi: new Set(["पर", "में"]),
  id: new Set(["dalam", "di", "ke_dalam"]),
  it: new Set(["a"]),
  ja: new Set(["で", "の中", "へ"]),
  ko: new Set(["안에", "에", "으로"]),
  ms: new Set(["dalam", "di", "ke_dalam"]),
  pl: new Set(["do", "przy", "w"]),
  pt: new Set(["dentro", "em"]),
  qu: new Set(["pi", "ukupi"]),
  ru: new Set(["в", "у"]),
  sw: new Set(["katika", "ndani"]),
  th: new Set(["ที่", "ใน"]),
  tl: new Set(["papasok_sa", "sa", "sa_loob"]),
  tr: new Set(["de", "içinde", "içine"]),
  uk: new Set(["в", "у"]),
  vi: new Set(["trong", "tại", "vào"]),
  zh: new Set(["在", "进入"]),
};

/**
 * A language's within-surfaces, with each one's spelling before a pronoun in
 * the case it takes (ru `в` → `во мне`, pl `w` → `we mnie`; M2 N3): a render
 * writes `<li/> во мне`, so the reader takes the vocalized marker there too.
 * Derived from the profile's `obliqueReferences`.
 */
const locativeSurfacesCache = new Map<string, ReadonlySet<string> | undefined>();
function locativeSurfaces(profile: LanguageProfile | undefined): ReadonlySet<string> | undefined {
  const code = profile?.code;
  if (!code) return undefined;
  if (locativeSurfacesCache.has(code)) return locativeSurfacesCache.get(code);
  const base = LOCATIVE_SURFACES[code];
  let surfaces = base;
  if (base && profile.obliqueReferences) {
    const all = new Set(base);
    for (const forms of Object.values(profile.obliqueReferences))
      for (const [marker, form] of Object.entries(forms)) {
        if (!base.has(marker)) continue;
        for (const phrase of typeof form === 'string' ? [form] : [form.direction, form.location]) {
          if (!phrase) continue;
          const words = phrase.split(' ');
          if (words.length > 1) all.add(words[0]!.toLowerCase());
        }
      }
    surfaces = all;
  }
  locativeSurfacesCache.set(code, surfaces);
  return surfaces;
}

/** English prepositions that mean `in` when they land in the locative slot. */
const ENGLISH_LOCATIVE_SENSES = new Set(['in', 'into', 'at', 'within', 'inside']);

/**
 * Render a positional expression's locative marker as English (`last <.message/>
 * in #chat`). en `in` passes through unchanged — it is not one of English's role
 * markers, which is why only other languages showed a leak here.
 */
export function toEnglishLocative(
  profile: LanguageProfile | undefined,
  marker: LanguageToken
): string {
  const surface = marker.value.toLowerCase();
  const langSurfaces = locativeSurfaces(profile);
  if (langSurfaces?.has(surface)) return 'in';

  const normalized = marker.normalized ?? marker.value;
  const lowered = normalized.toLowerCase();
  if (ENGLISH_LOCATIVE_SENSES.has(lowered)) return 'in';
  return ROLE_CONCEPT_TO_ENGLISH_LOCATIVE[lowered] ?? normalized;
}

/**
 * Whether `marker` is a "within" preposition: English `in` or the language's own.
 * The surface `in` counts whatever it normalizes to: renders write it in every
 * language, and de/it (`in`) and tr (a suffix of its `e`) read it as their
 * destination marker. A marker the pattern owes is still the pattern's.
 */
export function isLocativeMarker(
  profile: LanguageProfile | undefined,
  marker: LanguageToken
): boolean {
  if (marker.value.toLowerCase() === 'in') return true;
  const langSurfaces = locativeSurfaces(profile);
  if (langSurfaces?.has(marker.value.toLowerCase())) return true;
  return ENGLISH_LOCATIVE_SENSES.has((marker.normalized ?? marker.value).toLowerCase());
}

/** References a query can be scoped to (`<button/> in me`), by normalized form. */
const SCOPE_REFERENCES: ReadonlySet<string> = new Set([
  'me',
  'it',
  'you',
  'body',
  'document',
  'target',
  'result',
]);

/**
 * The scope a locative marker introduces, at `tokens[i]`: a reference (`me`),
 * an ancestor query (`closest <form/>`), or an element selector (`#list`; a `*`
 * style or `@` attribute never names an element). Its parts are English.
 */
export interface LocativeScope {
  readonly kind: 'reference' | 'closest' | 'selector';
  readonly parts: readonly PositionalRunPart[];
  readonly consumed: number;
}

function matchScopeAt(tokens: readonly LanguageToken[], i: number): LocativeScope | null {
  const t = tokens[i];
  if (!t) return null;
  if (t.kind === 'selector') {
    return /^[*@]/.test(t.value)
      ? null
      : { kind: 'selector', parts: [{ text: t.value, token: t }], consumed: 1 };
  }
  const norm = (t.normalized ?? t.value).toLowerCase();
  if (SCOPE_REFERENCES.has(norm)) {
    return { kind: 'reference', parts: [{ text: norm, token: t }], consumed: 1 };
  }
  const sel = tokens[i + 1];
  if (norm === 'closest' && sel?.kind === 'selector' && !/^[*@]/.test(sel.value)) {
    return {
      kind: 'closest',
      parts: [
        { text: 'closest', token: t },
        { text: sel.value, token: sel },
      ],
      consumed: 2,
    };
  }
  return null;
}

const POSITIONAL_HEADS: ReadonlySet<string> = new Set([
  'first',
  'last',
  'next',
  'previous',
  'random',
]);

/**
 * A query with its scope written before it, at `tokens[at]`, as a verb-final
 * language writes a modifier (`profile.queryScopeFirst`): tr `bendeki <p/>`
 * (`ben` `içindeki` `<p/>`), hi `मुझ में स्थित <p/>`, `#list の中の <li/>`, also
 * after a positional word (`bendeki ilk <p/>`). `phrase` is the marker and
 * participle (`में स्थित`), matched by the tokens' text however the tokenizer
 * cut it (ja `の中` `の`).
 */
export function matchScopeFirstQuery(
  tokens: readonly LanguageToken[],
  at: number,
  phrase: string
): {
  readonly scope: LocativeScope;
  readonly positional?: string | undefined;
  readonly query: LanguageToken;
  readonly consumed: number;
} | null {
  const scope = matchScopeAt(tokens, at);
  const target = phrase.replace(/\s+/g, '');
  if (!scope || !target) return null;
  let q = at + scope.consumed;
  let joined = '';
  while (
    joined.length < target.length &&
    tokens[q] &&
    target.startsWith(joined + tokens[q]!.value)
  ) {
    joined += tokens[q]!.value;
    q++;
  }
  if (joined !== target) return null;
  const head = (tokens[q]?.normalized ?? tokens[q]?.value ?? '').toLowerCase();
  const positional = POSITIONAL_HEADS.has(head) ? head : undefined;
  if (positional) q++;
  const query = tokens[q];
  if (query?.kind !== 'selector') return null;
  const isQuery = query.value.startsWith('<') || query.value.startsWith('.');
  if (!isQuery) return null;
  return { scope, positional, query, consumed: q + 1 - at };
}

/**
 * A query's locative scope, at `tokens[at]`: `<locative> <scope>`, as in
 * `add @disabled to <button/> in me`. Read only right after a `<…/>` query,
 * the one value `in` can scope, and only on a real locative (English `in`, or
 * the language's own from LOCATIVE_SURFACES): unlike the positional run's
 * source clause, nothing but the query anchors this slot. A marker the
 * enclosing pattern is about to require is its, not the query's (`poner <b/> en
 * #x`, es put … into …), unless something after the scope can satisfy it.
 */
export function matchQueryScope(
  tokens: readonly LanguageToken[],
  at: number,
  profile: LanguageProfile | undefined,
  isMarkerOwedByPattern?: (token: LanguageToken | undefined) => boolean
): { readonly marker: LanguageToken; readonly scope: LocativeScope } | null {
  const marker = tokens[at];
  if (!marker || !isLocativeMarker(profile, marker)) return null;
  const scope = matchScopeAt(tokens, at + 1);
  if (!scope) return null;
  const after = tokens[at + 1 + scope.consumed];
  // A collection's filter after the scope continues the value (`put <span/> in
  // #box where it matches .a into me`, P57/P62): the marker comes after it.
  const filter =
    !!after &&
    expressionWordOf(
      profile?.code ?? 'en',
      { tokens, i: at + 1 + scope.consumed },
      undefined
    ).toLowerCase() === 'where';
  if (
    isMarkerOwedByPattern?.(marker) === true &&
    isMarkerOwedByPattern(after) !== true &&
    !filter
  ) {
    return null;
  }
  return { marker, scope };
}

/**
 * Positional query keywords (English + the normalized forms the tokenizers
 * produce for every language, e.g. ar آخر→last, tl huli→last).
 *
 * `closest` is included: `closest <sel>` is the ancestor-scope query form
 * (`hide closest .modal`) and folds to the same call-expression shape the
 * runtime's positional expressions evaluate.
 *
 * Lives here rather than on PatternMatcher because BOTH expression seams need it
 * — the role capture and the raw-expression join (see {@link matchPositionalRun}).
 */
export const POSITIONAL_KEYWORDS: ReadonlySet<string> = new Set([
  'first',
  'last',
  'next',
  'previous',
  'random',
  'closest',
]);

/**
 * Positional heads whose SURFACE doubles as another sense in the profile, so
 * the tokenizer normalizes them away from the positional: bn `শেষ` is
 * registered as `end` (every block terminator) but heads positional runs as
 * `last` — `শেষ <button/> এ .modal` = "last <button/> in .modal" (focus-trap,
 * last-in-collection). Consulted by surface and ONLY inside
 * {@link matchPositionalRun}, and only when the following selector is an
 * ANGLE-BRACKET element query (`<button/>`, `<.message/>`): a bare `.`-class
 * after the head is not enough — behavior-sortable's repeat body captures the
 * block-end `শেষ` adjacent to the next clause's `.{dragClass}` patient, and an
 * unrestricted dual minted a phantom `last` there (`remove last .{dragClass}`,
 * valid but silently wrong). The corpus's positional collection queries all
 * author the element-query form, so the gate costs nothing.
 */
const POSITIONAL_HEAD_DUALS: Record<string, Record<string, string>> = {
  bn: { শেষ: 'last' },
};

/**
 * Normalized command-action keywords (the schema registry's action names).
 * Tokenizers normalize every language's command verbs to these forms, so the set
 * is language-independent. Used to keep a positional source clause from consuming
 * a following command's verb as a locative marker.
 */
export const COMMAND_ACTION_KEYWORDS: ReadonlySet<string> = new Set(
  Object.keys(commandSchemas).map(a => a.toLowerCase())
);

/**
 * One emitted piece of a positional run: the English text, and the token it came
 * from.
 *
 * The TOKEN is carried rather than a bare string because the two callers join
 * differently and only one of them can use a plain space. The positional ROLE
 * capture joins with `' '`; {@link joinExpressionTokens} needs the SOURCE POSITION
 * to decide whether a `.`-prefixed member glues to its object (`<input/>.value`)
 * or stays separated (`I match .active`). A string-only return would silently
 * un-glue every member chain in a condition.
 */
export interface PositionalRunPart {
  readonly text: string;
  readonly token: LanguageToken;
}

export interface PositionalRun {
  readonly parts: readonly PositionalRunPart[];
  /** Tokens consumed, counting from `start`. */
  readonly consumed: number;
}

/**
 * Recognize a positional query run at `tokens[start]`:
 *   `<positional> <selector> [.member]* [<locative-marker> <source-selector>]`
 * e.g. `last <button/> in .modal`, `آخر <.message/> في #chat`.
 *
 * Index-based and NON-CONSUMING so both expression seams can share it: the role
 * capture (`PatternMatcher.tryMatchPositionalExpression`) counts `consumed` off a
 * TokenStream, and the raw-expression join walks an array. Returns null — touching
 * nothing — unless the run's shape is present in full.
 *
 * This run is the ANCHOR that vouches for the locative surface. {@link
 * LOCATIVE_SURFACES} deliberately carries genuinely ambiguous free tokens (tr `de`,
 * pl `do`, es `en`), which are safe ONLY because they are read here, in a slot whose
 * sense is fixed. Never read that table outside this run.
 */
export function matchPositionalRun(
  tokens: readonly LanguageToken[],
  start: number,
  profile: LanguageProfile | undefined,
  /**
   * "The enclosing pattern requires this exact token next." Supplied by the role
   * capture, which knows the pattern token following the slot it is filling; the
   * raw-expression join has no pattern context and omits it. See the source
   * clause below for what it disambiguates.
   */
  isMarkerOwedByPattern?: (token: LanguageToken | undefined) => boolean
): PositionalRun | null {
  const head = tokens[start];
  if (!head) return null;

  const norm = (head.normalized ?? head.value).toLowerCase();
  const dualNext = tokens[start + 1];
  const dualHead =
    profile?.code && dualNext?.kind === 'selector' && dualNext.value.startsWith('<')
      ? POSITIONAL_HEAD_DUALS[profile.code]?.[head.value.toLowerCase()]
      : undefined;
  if (
    !POSITIONAL_KEYWORDS.has(norm) &&
    !POSITIONAL_KEYWORDS.has(head.value.toLowerCase()) &&
    dualHead === undefined
  ) {
    return null;
  }

  // The captured text is evaluated by the core's English positional expression
  // parser (`next(...)`, `closest(...)`), so the positional KEYWORD must be its
  // normalized English form — a source-language keyword (`cercano`, `次`,
  // `التالي`) is unevaluable as-is and the runtime either errors, drops to `me`,
  // or matches every element.
  const parts: PositionalRunPart[] = [
    { text: dualHead ?? head.normalized ?? head.value, token: head },
  ];
  let i = start + 1;

  // Required: the queried selector (e.g. <.message/>, .message, <button/>). Not
  // a comparison that some tokenizers lex as a selector: tr `son < 3` (a
  // variable spelled like tr's end word, whose reading is `last`) joined as
  // `last <` (PR 112).
  // `closest @foo` (the nearest element with the attribute, upstream's form) queries an
  // attribute, which every tokenizer reads as an identifier: without it the run declined
  // and sw's `karibu` (a particle) stayed in the value, `(karibu @foo to #d1)`.
  const sel = tokens[i];
  const attributeQuery =
    (dualHead ?? head.normalized ?? head.value).toLowerCase() === 'closest' &&
    sel?.kind === 'identifier' &&
    /^@[\w-]/.test(sel.value);
  if (!sel || (sel.kind !== 'selector' && !attributeQuery) || COMPARISON_OPERATORS.has(sel.value))
    return null;
  parts.push({ text: sel.value, token: sel });
  i++;

  // Optional member access on the queried element: `.value`, `.textContent`
  // (`previous <input/>.value`). The tokenizer emits these as `.`-prefixed
  // selector tokens; fold them into the expression so the whole lvalue is one
  // value.
  let memberGuard = 0;
  while (memberGuard++ < 8) {
    const member = tokens[i];
    if (member && member.kind === 'selector' && member.value.startsWith('.')) {
      parts.push({ text: member.value, token: member });
      i++;
    } else {
      break;
    }
  }

  // Optional source clause: <marker> <source-selector> (in #chat / في #chat /
  // sa_loob #chat). The marker may be a keyword, particle, or (TL) identifier;
  // only consumed when a selector follows, so a trailing command keyword is never
  // swallowed.
  const marker = tokens[i];
  const source = tokens[i + 1];
  if (
    marker &&
    source &&
    source.kind === 'selector' &&
    // A `*` style property or `@` attribute tokenizes as a selector but never
    // names an ELEMENT, so it cannot be a locative source. Without this, the
    // owner-first possessive `前 <div/> の *--x` read `の *--x` as "in *--x" and
    // the run swallowed the property it owns.
    !/^[*@]/.test(source.value) &&
    (marker.kind === 'keyword' || marker.kind === 'particle' || marker.kind === 'identifier') &&
    !POSITIONAL_KEYWORDS.has((marker.normalized ?? marker.value).toLowerCase()) &&
    // A command verb is never a locative marker: in a juxtaposed body
    // (`hide closest .modal remove .modal-open from body`) the next clause's verb
    // (`remove`) would otherwise be swallowed as the source marker and the
    // following command lost.
    !COMMAND_ACTION_KEYWORDS.has((marker.normalized ?? marker.value).toLowerCase()) &&
    // Nor is it the marker the enclosing pattern is about to REQUIRE and would
    // then be unable to find. A verb-final clause puts the positional phrase in
    // a MARKED role, so the run's own `<marker> <selector>` window lands on the
    // role marker that TERMINATES the run plus the NEXT role's value: bn
    // `নিকটতম .card তে .expanded কে টগল` ("toggle .expanded on closest .card")
    // read `তে .expanded` as "in .expanded", so `toggle-event-bn-sov`'s
    // `[{destination} তে]` group lost its marker, the whole run fell into
    // `{patient}`, and BOTH roles vanished — the same shape in ja/ko/tr/zh
    // across the toggle/add/remove positional rows.
    //
    // Membership in LOCATIVE_SURFACES cannot decide this: 17 of 22 languages
    // spell a role marker and their own locative alike, so gating on the table
    // fixes ja and not ko. Ownership can, and the second half is what keeps it
    // exact. tr's destination marker `e` lists `in` among its suffix
    // alternatives, so in `sonuncu <.message/> in #chat e kaydırma` the run's
    // English `in` LOOKS owed — but the real `e` sits right after `#chat`, so
    // the pattern loses nothing by letting the run have its source clause.
    // Refuse only when the marker is owed AND nothing after the source selector
    // could satisfy the same requirement, which is precisely the case where
    // consuming it strands the enclosing pattern.
    !(isMarkerOwedByPattern?.(marker) === true && isMarkerOwedByPattern(tokens[i + 2]) !== true)
  ) {
    // Marker is a locative keyword/particle (`in`/`from`/في/から) the English
    // runtime reads — normalize it; the source selector is code. Role markers
    // normalize to the ROLE NAME rather than an English word, so es `en` has to be
    // mapped back to the locative sense the slot carries (`in`) — otherwise this
    // renders `last <.message/> destination #chat`.
    //
    // `to` is `closest X to Y`'s own word, which every language writes in English; read
    // as a locative, pl's `to` (its word for `it`) came back `closest .bar it .foo`.
    const englishTo = marker.value.toLowerCase() === 'to';
    parts.push({ text: englishTo ? 'to' : toEnglishLocative(profile, marker), token: marker });
    parts.push({ text: source.value, token: source });
    i += 2;
  } else {
    // A reference or ancestor scope (`first <input/> in closest <form/>`, `last
    // <li/> in me`), which the selector-only clause above never took. Only on a
    // real locative: a particle before a reference is as often the next role's
    // marker (ja `を 自分`).
    const scoped = matchQueryScope(tokens, i, profile, isMarkerOwedByPattern);
    if (scoped && scoped.scope.kind !== 'selector') {
      parts.push({ text: 'in', token: scoped.marker }, ...scoped.scope.parts);
      i += 1 + scoped.scope.consumed;
    }
  }

  return { parts, consumed: i - start };
}

/**
 * Map an expression connective surface to its English keyword (`como` → `as`).
 * An unlisted surface — already English, or a word the lexicon does not carry —
 * is returned unchanged.
 */
export function translateConnective(languageCode: string, surface: string): string {
  const map = CONNECTIVE_LEXICON[languageCode];
  if (!map) return surface;
  return map[surface.toLowerCase()] ?? surface;
}

/**
 * Map a possessive property surface to its English DOM-property name.
 *
 * Only consulted at a slot already determined to be a property head (inside the
 * possessive matchers), so it is safe: an unlisted surface — an English property
 * (`value`, `innerHTML`), a member-access chain (`.style.display`), or any word
 * with no dictionary entry — is returned unchanged. English (`en`) and any
 * language whose property surface equals English have no table and pass through.
 */
export function translatePropertyName(languageCode: string, surface: string): string {
  const map = PROPERTY_NAME_LEXICON[languageCode];
  if (!map) return surface;
  return map[surface.toLowerCase()] ?? surface;
}

/**
 * Whether the language's own table names this surface as a DOM property.
 *
 * Unlike {@link translatePropertyName}, which is consulted at a slot already
 * known to be a property head and so may pass anything through, this is a
 * VOUCHER: it is what lets the possessive matcher accept a `keyword` token as
 * the property. A bare `identifier` after a possessive marker can only be a
 * property, but a keyword could equally be a command verb (`#button の
 * 切り替え`), so it is admitted only when the table says the word is a property
 * name — vi `giá trị`, th `ค่า`, tl `halaga`.
 */
export function isKnownPropertySurface(languageCode: string, surface: string): boolean {
  return PROPERTY_NAME_LEXICON[languageCode]?.[surface.toLowerCase()] !== undefined;
}

/**
 * Read a token as a possessive, by BOTH its native surface and its normalized
 * form. Profiles key `possessive.keywords` by the native word (en `my`, ar `لي`,
 * ko `내`), but most non-English tokenizers normalize that word before it gets
 * here — so checking only one direction misses half the languages. Mirrors the
 * lookup in `PatternMatcher.tryMatchPossessiveExpression`.
 */
function possessiveReferenceOf(profile: LanguageProfile, token: LanguageToken): string | undefined {
  const candidates = [token.value, token.normalized].filter(Boolean) as string[];
  for (const candidate of candidates) {
    const ref =
      getPossessiveReference(profile, candidate) ??
      getPossessiveReference(profile, candidate.toLowerCase());
    if (ref) return ref;
  }
  return undefined;
}

/**
 * Whether a token can be the bare-word head of an of-possessive. Sigil-prefixed
 * tokens are code the author chose (`$var`, `#id`, `.cls`, `*style`, `:ref`) and
 * grouping/operator tokens are structure — none is a property name.
 */
function isBareWordHead(token: LanguageToken): boolean {
  if (token.kind === 'literal' || token.kind === 'operator' || token.kind === 'punctuation') {
    return false;
  }
  return !/^[$:@#.*^(){}[\]]/.test(token.value);
}

/**
 * Whether a possessive surface ALSO serves as one of the profile's bare
 * references (ms `ia`, qu `chay` are both `it` and `its`). Such a token glued
 * to a `.member` is already the valid `it.prop`; the possessive dot-member
 * branch must decline it so those renders stay byte-identical.
 */
function isAlsoBareReference(profile: LanguageProfile | undefined, token: LanguageToken): boolean {
  const refs = profile?.references;
  if (!refs) return false;
  const surfaces = Object.values(refs)
    .filter((v): v is string => typeof v === 'string')
    .map(v => v.toLowerCase());
  const candidates = [token.value, token.normalized].filter(Boolean) as string[];
  return candidates.some(c => surfaces.includes(c.toLowerCase()));
}

/**
 * Whether a token can be the PROPERTY of a possessive that precedes it.
 *
 * This is what makes the possessive anchor safe, and it is not optional: in
 * several languages the possessive surface and the bare reference are the SAME
 * word (ms `ia`, qu `chay` are both `its` and `it`). Reading such a token as a
 * possessive wherever it appears rewrote `if it` (a truthiness check on the fetch
 * result) into the invalid `if its`. English never shows this — `it` is not among
 * its possessive keywords — so the collision only surfaces in other languages.
 *
 * A possessive is only a possessive when a property actually follows it.
 */
function isPropertyHeadCandidate(token: LanguageToken, languageCode: string): boolean {
  if (!isBareWordHead(token)) return false;
  // A surface the lexicon knows is a property is definitive, whatever its kind
  // (vi `giá trị` tokenizes as one keyword, not an identifier).
  if (translatePropertyName(languageCode, token.value) !== token.value) return true;
  // A connective never is: id `saya bukan cocok .x` (I do not match .x) read
  // as `my bukan`, its `not` taken for a property of `me`.
  if (translateConnective(languageCode, token.value) !== token.value) return false;
  // Otherwise a keyword carries a structural sense — a command verb, connective
  // or marker (ms `tetapkan` = `set`) — and is never a property name.
  return token.kind === 'identifier';
}

/**
 * Deliberately-ambiguous surfaces resolved by LOCAL CONTEXT at the render seam.
 *
 * Each word here was excluded from profile/tokenizer registration because it
 * carries TWO senses and a blanket binding rewrites the other one (the copula
 * slice's named exclusions: ar هو=it/is, hi है=is/has, th เป็น=is/as; the
 * no/without and exists/has dict duals; ja 空, where registering `empty` mints a
 * phantom COMMAND because `empty` is also an ActionType). This seam is the one
 * place where the word and its neighbors are visible together and no pattern
 * generation is involved — a context-gated translation here cannot mint a
 * phantom command and cannot touch the other sense, because nothing is emitted
 * unless the neighbor shape vouches for exactly one reading.
 *
 * Every gate was measured against the full corpus census (every occurrence of
 * every word, probe 2026-07-19): the other-sense occurrences all fail these
 * gates (ar it-sense هو precedes markers/verbs/`هو`, never a predicate; hi
 * not-sense नहीं precedes the keyword फेंकें→throw, never a bare identifier;
 * tl `walang_laman` is a different whole token). A miss leaves the pre-existing
 * behavior byte-identical (keyword-normalized or verbatim fallthrough).
 */
interface AmbiguousSenseRule {
  /** Emitted when the NEXT token is predicate-shaped: a literal (quoted string
      or number) or a keyword normalizing to empty/null/undefined/true/false. */
  beforePredicate?: string;
  /** Emitted when the NEXT token is a conversion type name (`Number`, `json`). */
  beforeTypeName?: string;
  /** Emitted when the NEXT token is a bare ASCII identifier (`dragHandle`,
      `:items`, `$n`) — the word shape is required because some tokenizers lex
      `(`/`)` as identifier-kind too. */
  beforeBareIdentifier?: string;
  /** Emitted when the NEXT token is a selector (`.w`, `#a`, `<p/>`): zh
      `没有 .w` and tl `walang .w` are `no .w`, as `没有 flag` is `no flag`. */
  beforeSelector?: string;
  /** Emitted when the PREVIOUS token is a selector or reference
      (`#modal আছে` → `#modal exists`). */
  afterSubject?: string;
  /** Emitted when the previous word, as this join read it, is `is` or `not`
      (ja `である 空` → `is empty`, `ではない 空` → `not empty`; ar `هو فارغ`
      → `is empty`, where `هو` read as `is` by its own rule), or the `does`/`do`
      of `does not match`. Checked before `beforeBareIdentifier`. */
  afterCopula?: string;
  /** Emitted when the PREVIOUS token is an operand (a word, selector, literal
      or reference, or a closing paren): the copula between the two sides of a
      comparison (ar `p هو q`, `p هو greater than q`). The pronoun sense opens a
      clause (`إذا هو اضبط …`, if it set …) or follows an operator (`"$" + هو`,
      whose `+` some tokenizers lex as an identifier, hence the word shape). */
  afterOperand?: string;
  /** Emitted when the NEXT token is a class reference (`.x`), ahead of
      `afterOperand`: hi `है` is both `is` and `has`, and only `has` takes a
      class (`#d1 है .x` → `#d1 has .x`). */
  beforeClassRef?: string;
  /** Emitted when the word stands between two operands, each a word, a
      literal (a keyword `true`, `null` … too), a selector, a reference or a
      bracket: sw `na` is both `and` and the `with` marker (`kweli na flag`,
      true and flag). A command whose own role takes `na` stops its value at
      the marker before the join sees it. */
  betweenOperands?: string;
  /** Emitted when the NEXT token starts an operand: qu `mana` is both
      `not` and `false`, and `false` never takes an operand (`2 kanqa mana 6`,
      2 is not 6; `mana flag`, not flag). */
  beforeOperand?: string;
  /** Emitted when the NEXT token is a comparison word with an operand on
      its left (`matches`): qu `noqa mana tupan .x`, `I do not match .x`, which
      a translation writes `me not matches .x` (value-lexicon.ts). */
  beforeComparison?: string;
  /** Emitted when none of the above applies, where the word has no reading
      of its own to fall through to: sw `tupu` is `empty` after a copula and
      `null` anywhere else in a value (its tokenizer reads it as `empty`). */
  otherwise?: string;
}

/** Keywords that are whole operands: literals and references. */
const OPERAND_KEYWORDS = new Set([
  'true',
  'false',
  'null',
  'undefined',
  'me',
  'my',
  'it',
  'its',
  'you',
  'your',
  'result',
  'event',
  'target',
  'body',
  'detail',
  'document',
  'window',
]);

function isOperandToken(token: LanguageToken): boolean {
  if (token.kind === 'literal' || token.kind === 'selector') return true;
  if ((token.kind as string) === 'reference') return true;
  if (token.kind === 'identifier') return OPERAND_WORD.test(token.value);
  // An English keyword in a translation (ar and id read `null` so) may carry no
  // normalized form: its value is the word.
  return (
    token.kind === 'keyword' &&
    OPERAND_KEYWORDS.has((token.normalized ?? token.value).toLowerCase())
  );
}

/** Does `token` end an operand: an operand, or a closing bracket? */
function endsOperand(token: LanguageToken | undefined): boolean {
  return !!token && (token.value === ')' || token.value === ']' || isOperandToken(token));
}

/** Does `token` start one: an operand, an opening bracket, or a unary `-`,
    `not` or `no`? */
function startsOperand(token: LanguageToken | undefined): boolean {
  if (!token) return false;
  if (/^[([-]$/.test(token.value) || isOperandToken(token)) return true;
  const normalized = (token.normalized ?? '').toLowerCase();
  return token.kind === 'keyword' && (normalized === 'not' || normalized === 'no');
}

/** A class reference (`.x`): what `has` takes, and a comparison does not. */
const CLASS_REF = /^\.[\p{L}_-][\p{L}\p{N}_-]*$/u;

/** A word-shaped identifier (`p`, `value`, `:count`, `$total`, `^color`), not an
    operator symbol that a tokenizer lexed as an identifier. A word may carry
    combining marks: hi `मान` (value) spells its vowel with one. */
/** A variable's name, or one the reader fused, `(si)` (registry.tokenize). An
    attribute (`@a`) too, where a tokenizer lexes it as an identifier (sw). */
const OPERAND_WORD =
  /^(?:[:$^]?[\p{L}_][\p{L}\p{M}\p{N}_]*|@[\p{L}_][\p{L}\p{M}\p{N}_-]*|\([\p{L}_$][\p{L}\p{M}\p{N}_$]*\))$/u;

/**
 * core's `has` (and the first person's `have`), in the language's own word,
 * before a class (`#a has .x`, `I have .x`): the
 * word is often a plain one (es `tiene`, pl `ma`), a variable's name (fr `a`)
 * or another concept's (tl `may`, tr `var`: `exists`), so it reads `has` only
 * where `has` can stand. hi `है` reads it already, and ar `لديه`/`لدي` are
 * whole-token keywords.
 */
const HAS: AmbiguousSenseRule = { beforeClassRef: 'has' };

const AMBIGUOUS_SENSES: Readonly<Record<string, Readonly<Record<string, AmbiguousSenseRule>>>> = {
  ar: {
    هو: { beforePredicate: 'is', afterOperand: 'is' },
    فارغ: { afterCopula: 'empty' },
  },
  hi: {
    है: { beforePredicate: 'is', beforeClassRef: 'has', afterOperand: 'is' },
    खाली: { afterCopula: 'empty' },
  },
  id: { kosong: { afterCopula: 'empty' }, punya: HAS },
  th: {
    เป็น: { beforeTypeName: 'as', beforePredicate: 'is', afterOperand: 'is' },
    ว่าง: { afterCopula: 'empty' },
    มี: HAS,
  },
  ja: { 空: { afterCopula: 'empty' }, ある: HAS },
  // he הוא is also the pronoun `it` (the tokenizer's alias), as ar هو is; ריק
  // is `null` elsewhere (the tokenizer's alias), as sw tupu is (M2 sheet B7).
  he: {
    הוא: { beforePredicate: 'is', afterOperand: 'is' },
    ריק: { afterCopula: 'empty' },
  },
  sw: {
    tupu: { afterCopula: 'empty', otherwise: 'null' },
    na: { betweenOperands: 'and' },
    ana: HAS,
    nina: HAS,
  },
  zh: { 没有: { beforeBareIdentifier: 'no', beforeSelector: 'no' }, 有: HAS },
  tl: {
    walang: { beforeBareIdentifier: 'no', beforeSelector: 'no' },
    may: { afterSubject: 'exists', beforeClassRef: 'has' },
  },
  bn: { আছে: { afterSubject: 'exists', beforeClassRef: 'has' }, আছি: HAS },
  qu: {
    mana: { beforeOperand: 'not', beforeComparison: 'not' },
    // qu's `where` is also the `target` reference: after a collection, the
    // filter (`items maypi chay tupan .a`, P57).
    maypi: { afterOperand: 'where' },
    chusaq: { afterCopula: 'empty' },
    kachkan: HAS,
    kachkani: HAS,
  },
  tr: {
    var: { afterSubject: 'exists', beforeClassRef: 'has' },
    boş: { afterCopula: 'empty' },
  },
  de: { hat: HAS, habe: HAS },
  es: { tiene: HAS, tengo: HAS },
  fr: { a: HAS, ai: HAS },
  it: { ha: HAS, ho: HAS },
  ko: { 있다: HAS },
  ms: { ada: HAS },
  pl: { ma: HAS, mam: HAS },
  pt: { tem: HAS, tenho: HAS },
  ru: { имеет: HAS, имею: HAS },
  uk: { має: HAS, маю: HAS },
  vi: { có: HAS },
};

/** Fetch's response types (core's `FetchResponseType`), which follow its `as`
    as a type name follows a conversion's: th `"/x" เป็น text` is `as text`. */
const RESPONSE_TYPE_NAMES = new Set(['text', 'json', 'html', 'response', 'blob', 'arraybuffer']);

/** Predicate values a copula can precede (mirrors the parser's
    CONDITION_PREDICATES plus the boolean literals). */
const SENSE_PREDICATE_NORMALIZED = new Set(['empty', 'null', 'undefined', 'true', 'false']);

/** Canonical `as` conversion targets: core's built-in converters
    (`defaultConversions`, which core's `conversions-direct-path` test holds
    this list to), plus both `json` casings (`fetch … as json` and `… as
    JSON`) and `FormData`. `Fixed` and `Fixed:<digits>` are resolved
    dynamically, as upstream resolves them: see `isConversionTypeName`. A
    non-English tokenizer splits a `:` name (`Fixed:2`, `Values:Form`) at its
    colon, and the matcher and the join put it back together. A type missing
    here loses more than its conversion: the matcher cannot read the value
    whole, so `put x as Boolean into #out` dropped the whole `put`.

    Not core's `Math`, which upstream does not have: th reads `เป็น` before a
    type name as `as`, so `6 เป็น Math.max(n, 1)` became `6 as Math`. */
export const CONVERSION_TYPE_NAMES = new Set([
  'Number',
  'Int',
  'Float',
  'String',
  'Boolean',
  'Date',
  'Array',
  'Object',
  'JSON',
  'json',
  'JSONString',
  'FormData',
  'FormEncoded',
  'HTML',
  'Fragment',
  'Values',
  'Values:Form',
  'Values:JSON',
  'Set',
  'Map',
  'Keys',
  'Entries',
  'Reversed',
  'Unique',
  'Flat',
]);

/** Does `name` name a conversion downstream reads? The pattern matcher's
    conversion fold and `readsAsOneExpression` must agree on this. A
    capitalized name does too: a type registered at runtime
    (`hyperfixi.config.conversions.MyType`, upstream's `_hyperscript.config.
    conversions`) is written that way, as every built-in one is, and semantic
    cannot see the registry. Unknown, `put x as MyType into #out` lost its
    whole `put`. (th's `เป็น` still reads `as` only before a built-in one.) */
export function isConversionTypeName(name: string): boolean {
  return isBuiltInConversion(name) || /^[A-Z][A-Za-z0-9_]*(?::[A-Za-z0-9_]+)?$/.test(name);
}

/** A conversion core has built in: a listed name, or `Fixed` and its digits. */
function isBuiltInConversion(name: string): boolean {
  return CONVERSION_TYPE_NAMES.has(name) || /^Fixed(?::\d+)?$/.test(name);
}

/** A token by where it stands: the list it was read from, and its index there. */
export interface TokenAt {
  readonly tokens: readonly LanguageToken[];
  readonly i: number;
}

function resolveAmbiguousSense(
  languageCode: string,
  at: TokenAt,
  prevText: string | undefined,
  afterNext: LanguageToken | undefined
): string | undefined {
  const token = at.tokens[at.i]!;
  const prev = at.tokens[at.i - 1];
  const next = at.tokens[at.i + 1];
  const rule = senseRuleOf(languageCode, token);
  if (!rule) return undefined;
  // Type name beats predicate for th เป็น: `เป็น Number` is a conversion even
  // though `Number` could look identifier-bare; the sets are disjoint anyway.
  // A type name that is called is a function: `6 เป็น String(n)` is `6 is
  // String(n)`, not `6 as String` and a stray `(n)`. A built-in name only
  // (`Fixed:2` too, which the tokenizer splits at its colon): `x เป็น Foo` can
  // compare `x` with a variable `Foo`.
  if (
    rule.beforeTypeName &&
    next?.kind === 'identifier' &&
    afterNext?.value !== '(' &&
    (isBuiltInConversion(next.value) || RESPONSE_TYPE_NAMES.has(next.value.toLowerCase()))
  ) {
    return rule.beforeTypeName;
  }
  if (
    rule.beforePredicate &&
    next &&
    (next.kind === 'literal' ||
      (next.kind === 'keyword' &&
        SENSE_PREDICATE_NORMALIZED.has((next.normalized ?? '').toLowerCase())))
  ) {
    return rule.beforePredicate;
  }
  if (
    rule.afterCopula &&
    prevText !== undefined &&
    ['is', 'not', 'does', 'do'].includes(prevText.toLowerCase())
  ) {
    return rule.afterCopula;
  }
  // A sigil variable is a bare name too: zh `没有 :checkboxes` is `no :checkboxes`.
  if (
    rule.beforeBareIdentifier &&
    next?.kind === 'identifier' &&
    /^[:$]?[A-Za-z_]\w*$/.test(next.value)
  ) {
    return rule.beforeBareIdentifier;
  }
  // An end word where a value stands is an operand too (C3): qu `mana tukuy`
  // (not tukuy), whose `tukuy` is also an end word.
  const nextEndWordOperand =
    !!next &&
    isEndWord(next, languageCode) &&
    endWordIsValue(endWordPlace(at.tokens, at.i + 1), languageCode);
  if (rule.beforeOperand && (startsOperand(next) || nextEndWordOperand)) {
    return rule.beforeOperand;
  }
  if (
    rule.beforeComparison &&
    next?.kind === 'keyword' &&
    COMPARISON_AFTER_OPERAND.has((next.normalized ?? '').toLowerCase())
  ) {
    return rule.beforeComparison;
  }
  if (rule.beforeSelector && next?.kind === 'selector') return rule.beforeSelector;
  // Before a class, `has`: `exists` takes no operand, so tl `#a may .x` is
  // `#a has .x` although `may` after a subject is `exists`.
  if (rule.beforeClassRef && next && CLASS_REF.test(next.value)) {
    return rule.beforeClassRef;
  }
  if (rule.afterSubject && (prev?.kind === 'selector' || (prev?.kind as string) === 'reference')) {
    return rule.afterSubject;
  }
  // An end word the join read as its own word is an operand (sw `mwisho na
  // flag`, mwisho and flag).
  const prevEndWordOperand = !!prev && isEndWord(prev, languageCode) && prevText === prev.value;
  if (rule.betweenOperands && (endsOperand(prev) || prevEndWordOperand) && startsOperand(next)) {
    return rule.betweenOperands;
  }
  // A keyword operand (`true`, `null`, `me`) and a closing bracket end an
  // operand too: ar `صحيح هو null` (true is null) is no pronoun.
  if (rule.afterOperand && (endsOperand(prev) || prev?.value === '}')) {
    return rule.afterOperand;
  }
  return rule.otherwise;
}

/** The sense table's rule for a word, if the language has one. */
function senseRuleOf(languageCode: string, token: LanguageToken): AmbiguousSenseRule | undefined {
  const table = AMBIGUOUS_SENSES[languageCode];
  return table?.[token.value] ?? table?.[token.value.toLowerCase()];
}

/**
 * The sense a deliberately-ambiguous word takes between `prev` and `next`, if
 * the table has one for it there (qu `mana` before an operand is `not`).
 */
export function ambiguousSenseOf(languageCode: string, at: TokenAt): string | undefined {
  return resolveAmbiguousSense(languageCode, at, undefined, undefined);
}

/** A token a conjunction can never follow: an operator, or an opening bracket or comma. */
function precedesOperand(value: string): boolean {
  return OPERATOR_CHARACTERS.test(value) || value === '(' || value === '[' || value === ',';
}

/**
 * A token a conjunction can never precede: a binary operator, or a closing
 * bracket or comma. Not `-`, which may be unary.
 */
function followsOperand(value: string): boolean {
  return (
    (BINARY_OPERATORS.has(value) && value !== '-') ||
    value === ')' ||
    value === ']' ||
    value === ','
  );
}

/**
 * Comparison words that take an operand on their left, as a conjunction
 * cannot. Not `equal`: `is greater than or equal to` puts a conjunction right
 * before it (es `mayor que o igual a`, it `maggiore o uguale a`).
 */
const COMPARISON_AFTER_OPERAND = new Set([
  'is',
  'am',
  'contains',
  'contain',
  'matches',
  'match',
  'has',
  'have',
  'includes',
  'include',
  'exists',
  'exist',
  'mod',
]);

/**
 * A keyword that joins conditions (`and`, `or`) is an OPERAND where a
 * conjunction cannot stand: after an operator (`x - i`), before one (`i < 3`,
 * `i is 2`), or inside brackets and lists (`(i + 1)`, `[i, 2]`). It is then a
 * variable the language spells like its conjunction: pl `i` ("and") is the
 * usual loop variable, es `y` ("and") a coordinate.
 */
export function isConnectiveOperand(
  languageCode: string,
  token: LanguageToken,
  prev: LanguageToken | undefined,
  next: LanguageToken | undefined
): boolean {
  // A connective by the tokenizer (pl `i`, a keyword) or by the lexicon
  // (pt/fr `ou`, an identifier the join reads as `or`).
  const sense =
    token.kind === 'keyword'
      ? (token.normalized ?? '').toLowerCase()
      : translateConnective(languageCode, token.value).toLowerCase();
  if (sense !== 'and' && sense !== 'or') return false;
  // First, it has nothing on its left to join: alone, an `if` condition that
  // is only the word (es `si y`); before an operator, the value's first
  // operand (pl `ustaw do x i - 1`, set x to i - 1, where `-` may be unary and
  // so reads no operand before it).
  if (prev === undefined) return true;
  if (prev !== undefined && precedesOperand(prev.value)) return true;
  if (next === undefined) return false;
  if (followsOperand(next.value)) return true;
  const nextWord =
    next.kind === 'keyword'
      ? (next.normalized ?? next.value).toLowerCase()
      : translateConnective(languageCode, next.value).toLowerCase();
  return COMPARISON_AFTER_OPERAND.has(nextWord);
}

/** The verbs of core's `does not …` phrases, which follow the negation in English. */
const DOES_NOT_VERBS: ReadonlySet<string> = new Set(['include', 'contain', 'match', 'exist']);

/** Is `word` (a word as the join read it) the copula of a comparison? */
function isCopulaWord(word: string | undefined): boolean {
  const lowered = word?.toLowerCase();
  return lowered === 'is' || lowered === 'not';
}

/**
 * Is the token at `i` the `in` of `is in` / `is not in`? The operator stays
 * English in every rendering, and in de and it `in` is also the `into` marker
 * (`setzen 2 ist in [1, 2, 6] in #out`), which the join read as `destination`
 * and a value's extent stopped at. Directly after the copula it is the
 * operator: no marker follows `is`.
 */
export function isCopulaIn(
  languageCode: string,
  tokens: readonly LanguageToken[],
  i: number
): boolean {
  const token = tokens[i];
  const prev = tokens[i - 1];
  if (!token || !prev || token.value.toLowerCase() !== 'in') return false;
  return isCopulaWord(expressionWordOf(languageCode, { tokens, i: i - 1 }, undefined));
}

/**
 * The English word the expression join writes for a token no anchor claims:
 * its sense when the word has two (ar `هو` between operands is `is`), else
 * the connective it spells (de `nicht`), else a keyword's normalized form or
 * the surface. Exported so a capture that must find where an expression ends
 * (the pattern matcher's operator run) reads each word as the join will.
 */
export function expressionWordOf(
  languageCode: string,
  at: TokenAt,
  prevText: string | undefined,
  afterNext?: LanguageToken
): string {
  const token = at.tokens[at.i]!;
  const prev = at.tokens[at.i - 1];
  const next = at.tokens[at.i + 1];
  // `equal to` stays English in every rendering (`is greater than or equal
  // to`), and its `to` is a word some languages have their own sense for: pl
  // reads it as `it`, so `p is equal to 1` came back `p is equal it 1`.
  if (prevText?.toLowerCase() === 'equal' && token.value.toLowerCase() === 'to') return 'to';
  // So does a collection's `mapped to` (P57): pl reads `to` as `it`.
  if (prevText?.toLowerCase() === 'mapped' && token.value.toLowerCase() === 'to') return 'to';
  if (isCopulaWord(prevText) && token.value.toLowerCase() === 'in') return 'in';
  // The verb of `does not include` (`contain`, `match`, `exist`) stays English
  // in every rendering. it's connective table reads `include` as `includes`
  // (its word for both), so `does non include "a"` joined as `does not
  // includes "a"`, and the `put` lost its value with its destination. PR 96.
  if (prevText?.toLowerCase() === 'not' && DOES_NOT_VERBS.has(token.value.toLowerCase())) {
    return token.value.toLowerCase();
  }
  const sense = resolveAmbiguousSense(languageCode, at, prevText, afterNext);
  if (sense !== undefined) return sense;
  if (isConnectiveOperand(languageCode, token, prev, next)) return token.value;
  const connective = translateConnective(languageCode, token.value);
  if (connective !== token.value) return connective;
  if (token.kind !== 'keyword') return token.value;
  // A role marker (normalized to its role, as tr `na` is `destination`) is no
  // expression vocabulary, so in an expression it is a variable spelled like
  // one: tr `değil na < 3` (not na < 3) joined as `not destination < 3`, de
  // `length of aus` as `length of source`. Not `event`, which is also the
  // event reference: de `ereignis's type` is `event's type`.
  const norm = (token.normalized ?? token.value).toLowerCase();
  if (norm !== 'event' && ROLE_MARKER_CONCEPTS.has(norm)) return token.value;
  // An end word where a value stands is a variable spelled like it (C3,
  // endWordIsValue): es `fin < 3` joined as `end < 3`, tr `son ve flag` (its
  // `son` reads `last`) as `last and flag`.
  if (
    isEndWord(token, languageCode) &&
    endWordIsValue(endWordPlace(at.tokens, at.i), languageCode)
  ) {
    return token.value;
  }
  return token.normalized ?? token.value;
}

/**
 * The index of the selector that ends a `<property> <of-marker>` run starting
 * at `start`, or undefined when none starts there. Every link, the first
 * included, takes a property word: an identifier (`value de #price`), or a
 * keyword the lexicon calls a property. tr's genitive `in` is also English
 * `in`, and `2 dir in [1, 2, 6]` (`2 is in …`) is no link, since `dir` is a
 * verb.
 */
export function ofChainEnd(
  tokens: readonly LanguageToken[],
  start: number,
  profile: LanguageProfile | undefined,
  languageCode: string
): number | undefined {
  const first = tokens[start];
  const marker = tokens[start + 1];
  if (!first || !marker || !isOfPossessiveMarker(profile, marker)) return undefined;
  // The first link may also be an attribute or a style, the outermost
  // property of an element (es `@role de #sr-announce`, M2 sheet A3).
  if (!isPropertyHeadCandidate(first, languageCode) && !SIGIL_PROPERTY.test(first.value)) {
    return undefined;
  }
  let k = start + 2;
  while (
    tokens[k] !== undefined &&
    tokens[k + 1] !== undefined &&
    isOfPossessiveMarker(profile, tokens[k + 1]!) &&
    isPropertyHeadCandidate(tokens[k]!, languageCode)
  ) {
    k += 2;
  }
  return tokens[k]?.kind === 'selector' ? k : undefined;
}

/** An attribute or a style reference (`@role`, `*display`). */
const SIGIL_PROPERTY = /^[@*][A-Za-z_-][\w-]*$/;

/** Control words and the copula, which are structure and never a value (see isStructureKeyword). */
const STRUCTURE_WORDS: ReadonlySet<string> = new Set([
  'if',
  'unless',
  'while',
  'until',
  'for',
  'when',
  'where',
  'is',
  'not',
  'no',
  'and',
  'or',
  'as',
]);

/**
 * A keyword that is structure: a role marker (normalized to its role, as tr
 * `na` is `destination`), a control word or the copula. Never a reference
 * (`me`, `event`), which is a value. Not a command verb, which is structure only
 * where no dropped command can have left it (see loneKeywordKind).
 */
function isStructureKeyword(token: LanguageToken): boolean {
  const norm = (token.normalized ?? token.value).toLowerCase();
  if (norm === 'event' || isValidReference(norm)) return false;
  return STRUCTURE_WORDS.has(norm) || ROLE_MARKER_CONCEPTS.has(norm);
}

/**
 * The words before which an end word is a value (endWordIsValue): an operand's
 * `and`, `or` or copula, and a loop's `times` (`repeat son times`, the count).
 */
const END_WORD_OPERANDS: ReadonlySet<string> = new Set(['and', 'or', 'is', 'times']);

/**
 * The words that owe the value after them (endWordIsValue), where no verb or
 * marker takes them as its own value. `not` owes one wherever it stands.
 */
const OWING_WORDS: ReadonlySet<string> = new Set([
  'if',
  'unless',
  'while',
  'until',
  'is',
  'and',
  'or',
  // A loop's or a membership's `in` (qu `repeat x ukupi tukuy`, for x in tukuy).
  'in',
]);

const isAction = (word: string): word is ActionType =>
  Object.prototype.hasOwnProperty.call(commandSchemas, word);

/** A command verb: a keyword, or a word the tokenizer normalized to one. */
function verbOf(token: LanguageToken): ActionType | undefined {
  if (token.kind !== 'keyword' && !(token.normalized && token.normalized !== token.value)) {
    return undefined;
  }
  const norm = (token.normalized ?? token.value).toLowerCase();
  return isAction(norm) ? norm : undefined;
}

const markersAfterVerbMemo = new WeakMap<readonly LanguagePattern[], Map<string, Set<string>>>();

/**
 * The markers a command's own patterns write right after its verb: it
 * `impostare in {destination} {patient}` (set), pl `ustaw do …`. Such a marker
 * right after the verb is the marker, not a variable the verb takes. With
 * `beforeRolePair`, only the markers a pattern follows with two roles, the
 * second unmarked (those two, the destination and then the patient).
 */
function markersAfterVerb(
  languageCode: string,
  action: ActionType,
  beforeRolePair = false
): ReadonlySet<string> {
  let patterns: readonly LanguagePattern[];
  try {
    patterns = getPatternsForLanguage(languageCode);
  } catch {
    return new Set();
  }
  let byAction = markersAfterVerbMemo.get(patterns);
  if (!byAction) {
    byAction = new Map();
    markersAfterVerbMemo.set(patterns, byAction);
  }
  const key = beforeRolePair ? `${action} before a role pair` : action;
  let markers = byAction.get(key);
  if (!markers) {
    markers = new Set();
    for (const pattern of patterns) {
      if (pattern.command !== action) continue;
      const [verb, after, first, second] = pattern.template.tokens;
      if (verb?.type !== 'literal' || after?.type !== 'literal') continue;
      if (beforeRolePair && (first?.type !== 'role' || second?.type !== 'role')) continue;
      for (const word of [after.value, ...(after.alternatives ?? [])]) {
        markers.add(word.toLowerCase());
      }
    }
    byAction.set(key, markers);
  }
  return markers;
}

const loopWordsMemo = new WeakMap<readonly LanguagePattern[], Set<string>>();

/**
 * The words a loop's own patterns write right before a role: fr `en` and
 * `tantque` (`repeat {patient} en {source}`, `repeat tantque {condition}`), tr
 * `içinde`, tl `sa_loob`. The render writes them as plain words no marker test
 * sees, and each owes the value after it, as a marker does (PR 121). Not the
 * profile's role markers, which have their own reading: where markers follow
 * values, a marker after its value owes nothing, and a command can end with one
 * (hi `sortable:move को ट्रिगर मैं में समाप्त`, trigger sortable:move on me, end).
 */
function loopWordsBeforeRole(languageCode: string): ReadonlySet<string> {
  let patterns: readonly LanguagePattern[];
  try {
    patterns = getPatternsForLanguage(languageCode);
  } catch {
    return new Set();
  }
  let words = loopWordsMemo.get(patterns);
  if (!words) {
    const markers = new Set(
      Object.values(tryGetProfile(languageCode)?.roleMarkers ?? {})
        .flatMap(marker => (marker ? [marker.primary, ...(marker.alternatives ?? [])] : []))
        .map(word => word.toLowerCase())
    );
    const found = new Set<string>();
    for (const pattern of patterns) {
      if (pattern.command !== 'repeat') continue;
      const tokens = pattern.template.tokens;
      tokens.forEach((token, i) => {
        if (token.type !== 'literal' || tokens[i + 1]?.type !== 'role') return;
        for (const word of [token.value, ...(token.alternatives ?? [])]) {
          if (!markers.has(word.toLowerCase())) found.add(word.toLowerCase());
        }
      });
    }
    words = found;
    loopWordsMemo.set(patterns, words);
  }
  return words;
}

/** A role marker: a particle, or a keyword normalized to the role it marks (fr `à`, de `auf`). */
function isMarker(token: LanguageToken): boolean {
  if (token.kind === 'particle') return true;
  return (
    token.kind === 'keyword' && ROLE_MARKER_CONCEPTS.has((token.normalized ?? '').toLowerCase())
  );
}

/** The English word a token spells in an expression: a keyword's normalized form, or a connective. */
function wordOf(token: LanguageToken, languageCode: string): string {
  if (token.kind === 'keyword' && token.normalized) return token.normalized.toLowerCase();
  return translateConnective(languageCode, token.value).toLowerCase();
}

/**
 * C3: is an end word a value — a variable spelled like it? A block's end
 * stands after a command's last word, and before a command, `then`, another end
 * or nothing. So an end word is a value:
 *
 * - before an operator, `and`, `or` or the copula (PR 112: tr `eğer son ve
 *   flag`, if son and flag, lost its whole `if`, and es `set x to fin < 3` read
 *   `set x to <`), or a possessive `'s` (`fin's length`);
 * - before its own case marker, where markers follow their values (tr `son i
 *   #out e koy`, put son into #out), unless another particle follows the
 *   marker, which is then the value (`son i i artır`, end, increment i);
 * - after an operator symbol or `not` (es `no`, pt `não`, qu `mana`);
 * - after a word that owes a value — `if`, `while`, the copula, `and` — and,
 *   where the verb comes first, after a command's verb when the command
 *   requires a role (es `poner fin en #out`, `incrementar fin`; hide's is
 *   optional, so `ocultar fin` ends), and, where markers come first, after a
 *   marker (`establecer x a fin`). Not where that word is itself a value, after
 *   a verb or a marker that takes it (es `incrementar a fin`, increment a, end,
 *   whose `a` is also a marker) — though a marker the verb's own patterns write
 *   right after it is the marker (it `impostare in fine 1`, set fine to 1).
 *
 * The block scan and its condition and branch splits, the handler body's
 * clause walk, the role capture (C3), the operator run and the join each read
 * an end word by it, so they agree (PR 112 read the first rule, PR 114 the
 * rest). Hand-written text only: the verified render writes `(fin)` where the
 * plain render would misread.
 */
export function endWordIsValue(place: EndWordPlace, languageCode: string): boolean {
  const { prev, beforePrev, next, afterNext } = place;
  if (next) {
    if (RUN_OPERATORS.has(next.value)) return true;
    if (END_WORD_OPERANDS.has((next.normalized ?? next.value).toLowerCase())) return true;
    if (next.value === "'" && afterNext?.value.toLowerCase() === 's') return true;
    // A word that is `and` only between two operands (sw `na`).
    const between = senseRuleOf(languageCode, next)?.betweenOperands;
    if ((between === 'and' || between === 'or') && startsOperand(afterNext)) return true;
  }
  const profile = tryGetProfile(languageCode);
  const markerFinal = !!profile && profile.markingStrategy !== 'preposition';
  if (markerFinal && next?.kind === 'particle' && afterNext?.kind !== 'particle') return true;
  if (!prev) return false;
  if (BINARY_OPERATORS.has(prev.value)) return true;
  // The last word of a view transition's head, `start [a] view transition`, which
  // is not the `transition` command's verb and owes no value: the end word
  // closes an empty block.
  if (prev.value.toLowerCase() === 'transition' && beforePrev?.value.toLowerCase() === 'view') {
    return false;
  }
  const word = wordOf(prev, languageCode);
  if (word === 'not' || word === 'no') return true;
  // A word that is `not` only before an operand (qu `mana`).
  if (senseRuleOf(languageCode, prev)?.beforeOperand === 'not') return true;
  const verbFirst = profile?.wordOrder !== 'SOV';
  // The value after a marker's value, where the verb's own pattern follows that
  // marker with two roles: it `impostare in x fine` (set x to fine) owes the
  // second, which no marker introduces (PR 122).
  const pairVerb = place.third ? verbOf(place.third) : undefined;
  if (
    pairVerb &&
    beforePrev &&
    markersAfterVerb(languageCode, pairVerb, true).has(beforePrev.value.toLowerCase())
  ) {
    return true;
  }
  // The word before is itself a value, a verb's or a marker's — unless it is
  // a marker the verb's own patterns write right after it (it `impostare in
  // fine 1`, set fine to 1).
  const takenBy = beforePrev && verbFirst ? verbOf(beforePrev) : undefined;
  if (
    (takenBy && !markersAfterVerb(languageCode, takenBy).has(prev.value.toLowerCase())) ||
    (beforePrev && !markerFinal && isMarker(beforePrev))
  ) {
    return false;
  }
  if (OWING_WORDS.has(word)) return true;
  // Not an SOV loop's own verb, which ends its head (PR 120): the end word
  // after it closes an empty loop (ja `3 回 を 繰り返し 終わり`), though a
  // for-in written verb-first puts a role there (PR 126). Nor a count word
  // after its count, which ends the head too: sw's word-first head writes
  // `mara` before the count (`rudia mara mwisho`, repeat mwisho times), but
  // the render writes it after (`rudia 3 mara mwisho`, an empty loop) (PR 131).
  const countWordAfterCount =
    !!beforePrev &&
    wordOf(beforePrev, languageCode) !== 'repeat' &&
    !!NATIVE_COUNT_WORDS[languageCode]?.includes(prev.value);
  if (
    loopWordsBeforeRole(languageCode).has(prev.value.toLowerCase()) &&
    (verbFirst || word !== 'repeat') &&
    !countWordAfterCount
  ) {
    return true;
  }
  const verb = verbFirst ? verbOf(prev) : undefined;
  if (verb && commandSchemas[verb].roles.some(role => role.required)) return true;
  return !markerFinal && isMarker(prev);
}

/**
 * What a keyword spells when it stands alone where a value must: `structure`
 * (above), `verb` (a command verb) or `event` (an event name); undefined for
 * any other keyword (a reference, a constant). A translation writes a variable
 * verbatim, so a keyword alone there names a variable the language spells like
 * it: es `si` is `if`, tr `al` is `get`, and joined as English they read `if
 * if`, `if get`. An event name is one too: upstream and the engine run `set
 * input to "a"` on a variable named `input`, which English's own parse dropped
 * (was OPEN_ITEMS P49). The one classifier the join (J1, below) and the role
 * capture (C8, `keywordIsVariable` in value-reading.ts) share: a whole joined
 * value is never what a dropped command leaves, so the join counts a verb; the
 * role capture only where no dropped command can have left it, and an event
 * name only where no event stands.
 */
export function loneKeywordKind(token: LanguageToken): 'structure' | 'verb' | 'event' | undefined {
  if (token.kind !== 'keyword') return undefined;
  if (isStructureKeyword(token)) return 'structure';
  const norm = (token.normalized ?? token.value).toLowerCase();
  if (COMMAND_ACTION_KEYWORDS.has(norm)) return 'verb';
  return EVENT_NAMES.has(norm) ? 'event' : undefined;
}

/**
 * Join a token slice into the English expression text stored on an `expression`
 * value — the shared seam for every raw-expression capture.
 *
 * Two independent joins used to exist with different rules: the conditional's
 * `joinTokenText` (keyword → `normalized`, else verbatim) and the operator-run
 * join (verbatim, no normalization and no `.`-glue). Both leaked foreign
 * vocabulary into text that the core expression parser then has to read as
 * English, so es `si mi valor es vacío` joined to `if me valor is empty` and
 * `"Hola, " + mi valor` joined to itself unchanged. Routing both here fixes the
 * foreign→English render AND the foreign→AST→execute path, which would otherwise
 * read a non-existent `.valor` DOM property.
 *
 * Translation is ANCHORED, never blanket:
 *   1. a possessive token → the English possessive adjective (`mi` → `my`), and
 *   2. the property head DIRECTLY after it, and only when the lexicon recognizes
 *      the surface (`valor` → `value`);
 *   3. a `<property> <of-marker> <selector>` run → `value of #price` (the
 *      of-marker is emitted structurally, because es spells `of` and `from` the
 *      same `de` — translating that surface freely would corrupt `from` clauses);
 *   4. a positional run → `last <button/> in .modal` ({@link matchPositionalRun},
 *      shared with the role capture in PatternMatcher so the two seams cannot
 *      disagree; the run is what vouches for the ambiguous locative surfaces);
 *   5. a connective the lexicon vouches for as unambiguous (`como` → `as`).
 * Everything else keeps the pre-existing rule. The anchors are what make this
 * safe: a bare `valor` elsewhere may be a user variable, and a string literal may
 * contain any word at all, so nothing is translated without a slot vouching for
 * it — the same guarantee that made the possessive-matcher pass safe (see
 * `translatePropertyName` above).
 */
export function joinExpressionTokens(
  tokens: readonly LanguageToken[],
  profile: LanguageProfile | undefined
): string {
  const languageCode = profile?.code ?? 'en';
  const [only] = tokens;
  // J1: a keyword alone is the variable it spells (loneKeywordKind), and so is
  // an end word: joined whole, it is a value (es `si fin poner …`, if fin).
  if (tokens.length === 1 && only && (loneKeywordKind(only) || isEndWord(only, languageCode))) {
    return only.value;
  }
  let out = '';
  let previous: LanguageToken | undefined;
  /** The text appended for `previous`: how this join read the last word. */
  let previousText: string | undefined;

  const append = (text: string, token: LanguageToken): void => {
    previousText = text;
    if (previous === undefined) {
      out = text;
      previous = token;
      return;
    }
    // A member access glues to its object with no space (`event.shiftKey`), but a
    // class selector in a comparison keeps it (`I match .active`). Both surface as
    // a `.`-prefixed token, so disambiguate by SOURCE adjacency. Without this the
    // expression renders `event .shiftKey`, which the canonical parser rejects.
    // A trailing `!` follows the same rule: `beep!` tokenizes as `beep` + `!`, and
    // the canonical parser rejects the spaced `beep !` — glue it only when the two
    // were source-adjacent (a spaced `a != b` keeps its space). Both are gated on
    // adjacency, so the possessive run `my value` (neither `.`- nor `!`-prefixed)
    // still spaces normally. A comma glues the same way (`'a', 'b'`, a list).
    const adjacent =
      previous.position?.end !== undefined &&
      token.position?.start !== undefined &&
      previous.position.end === token.position.start;
    // So does a possessive the tokenizer kept whole after a query (qu `<div/>'s`),
    // and an index against its name (`bar["count"]`): spaced, it is a list where
    // a value follows with no marker (it `impostare in element's bar ["count"] …`).
    const glue =
      adjacent &&
      (text.startsWith('.') ||
        text.startsWith('!') ||
        text.startsWith('[') ||
        text === ',' ||
        text === "'s");
    out += (glue ? '' : ' ') + text;
    previous = token;
  };

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const reference = profile ? possessiveReferenceOf(profile, token) : undefined;

    const next = tokens[i + 1];

    // `<input/> in the closest <table/>`, `checked of the :boxes`: the article
    // between a query's `in` and a positional, or between `of` and a variable,
    // is noise, as the engine reads it. Joined, a translation wrote it as
    // English (de `in the nächstgelegene <table/>`, ru `отмечено of the :boxes`)
    // and read it back as nothing (P57).
    const previousWord = previousText?.toLowerCase();
    if (
      token.value.toLowerCase() === 'the' &&
      next !== undefined &&
      ((previousWord === 'in' &&
        ['closest', 'next', 'previous', 'first', 'last'].includes(next.value.toLowerCase())) ||
        (previousWord === 'of' &&
          next.kind === 'identifier' &&
          /^[:$]?[A-Za-z_]\w*$/.test(next.value)))
    ) {
      continue;
    }

    // A conversion's `Fixed:2` or `Values:Form`, which a non-English tokenizer
    // splits at its colon: glued back as English writes it. The spaced `Fixed
    // : 2` reads on core but is not what the source said.
    if (
      token.value === ':' &&
      previous !== undefined &&
      previousText !== undefined &&
      next !== undefined &&
      previous.position?.end === token.position?.start &&
      token.position?.end === next.position?.start &&
      isConversionTypeName(`${previousText}:${next.value}`)
    ) {
      out += `:${next.value}`;
      previousText = `${previousText}:${next.value}`;
      previous = next;
      i += 1;
      continue;
    }

    // English `'s` possessive, tokenized as `'` + `s` glued to its owner
    // (`#price` `'` `s` `wartość`) — the shape every language that renders the
    // `'s` fallback produces (hi/pl/ru/uk/th/vi/it/ms/tl/he). Glue the marker
    // back onto the owner and translate the property noun through the lexicon,
    // so `#price's wartość` comes back as `#price's value` rather than a
    // non-ASCII identifier the canonical parser rejects ("Unknown token: ś").
    // A lone `'` token can only be this marker: a quoted string is one token.
    if (
      token.value === "'" &&
      previous !== undefined &&
      next?.value === 's' &&
      token.position?.end !== undefined &&
      next.position?.start === token.position.end
    ) {
      out += "'s";
      previous = next;
      const prop = tokens[i + 2];
      if (prop && translatePropertyName(languageCode, prop.value) !== prop.value) {
        append(translatePropertyName(languageCode, prop.value), prop);
        i += 2;
      } else {
        i += 1;
      }
      continue;
    }
    // Owner-first genitive, `<owner> <marker> <property>`: how a possessive
    // inside an expression renders wherever the possessive marker sits between
    // owner and property (ja `#d1のtextContent`, ko `#d1의 textContent`, vi
    // `#d1 của textContent`; renderPropertyPath). The owner-first anchor below
    // reads only a property word the lexicon translates (`#d1 の 値`), since its
    // marker test also admits a `from` marker, so an untranslated property kept
    // the marker: `if #d1's textContent is "z"` read back as `#d1 の textContent
    // is "z"`, which the expression parser took for the truthy `#d1`. The
    // profile's own between-position marker is always the genitive, so here any
    // property word qualifies. The `'s` form is the English reference's, and it
    // composes with a chained `'s` (`#d1のtextContent's length`). Gated on the
    // shape the renderer writes: a selector owner and a property word.
    const genitive = profile?.possessive;
    const genitiveProperty = tokens[i + 2];
    if (
      genitive?.markerPosition === 'between' &&
      genitive.marker &&
      token.kind === 'selector' &&
      next?.value === genitive.marker &&
      genitiveProperty !== undefined &&
      isPropertyHeadCandidate(genitiveProperty, languageCode)
    ) {
      append(`${token.value}'s`, token);
      append(translatePropertyName(languageCode, genitiveProperty.value), genitiveProperty);
      i += 2;
      continue;
    }
    // `<property> of <owner>` with an owner the renderer leaves in English
    // (`length of arr`, a variable owner, not a selector): the property word
    // localizes (ms `panjang of arr`) and nothing read it back, so the value
    // kept `panjang`, or in bn and th lost the whole value to the ASCII guard.
    // Anchored on English `of`, and read only when the lexicon names the word.
    if (
      next?.value.toLowerCase() === 'of' &&
      translatePropertyName(languageCode, token.value) !== token.value &&
      isPropertyHeadCandidate(token, languageCode)
    ) {
      append(translatePropertyName(languageCode, token.value), token);
      continue;
    }
    if (reference !== undefined && next) {
      // A possessor may be separated from its property by a CONNECTOR: id
      // `saya punya nilai` = "I have value" = "my value" (`punya` = "have/own").
      // The value-slot matcher (`tryMatchPossessiveExpression`) already skips the
      // connector via `profile.possessive.connectors`; this seam must too, or the
      // connector is read as the property head and the real property leaks
      // (`saya punya nilai` → `my punya nilai` in every id `if`/`+`/paren
      // expression). No connector → `propIdx` is `i + 1`, byte-identical to before.
      const connectors = profile?.possessive?.connectors;
      const skipsConnector =
        !!connectors?.length &&
        connectors.some(c => {
          const cl = c.toLowerCase();
          return cl === next.value.toLowerCase() || cl === (next.normalized ?? '').toLowerCase();
        });
      const propIdx = skipsConnector ? i + 2 : i + 1;
      const prop = tokens[propIdx];
      // After a connector an `@` attribute or `*` style property is the property too:
      // id `saya punya @data-x` is `my @data-x`, and it read back `me punya @data-x`.
      // Only after the connector, the shape the renderer writes for a possessive.
      const sigilProperty = skipsConnector && prop !== undefined && /^[@*][\w-]/.test(prop.value);
      if (prop && (sigilProperty || isPropertyHeadCandidate(prop, languageCode))) {
        append(getEnglishPossessiveAdjective(reference), token);
        append(sigilProperty ? prop.value : translatePropertyName(languageCode, prop.value), prop);
        i = propIdx;
        continue;
      }
      // A dot-member can also carry the property when a connector intervenes:
      // qu `chaypaq.error` = chay(→it) + paq(connector) + `.error`. The
      // value-slot matcher accepts the `.`-selector shape (dot stripped →
      // `its error`), but the bare-word guard above declines it, so the
      // connector leaked glued to the member (`if it paq.error`). Source
      // adjacency to the connector is what vouches for member access — the same
      // rule the appender's `.`-glue uses; a spaced `.cls` stays a class
      // selector. Gated on the connector: without one the plain `.`-glue
      // already renders the valid `it.prop`, and this branch must not rewrite it.
      if (
        skipsConnector &&
        prop &&
        /^\.[a-zA-Z_]\w*$/.test(prop.value) &&
        next.position?.end !== undefined &&
        prop.position?.start !== undefined &&
        next.position.end === prop.position.start
      ) {
        append(getEnglishPossessiveAdjective(reference), token);
        append(translatePropertyName(languageCode, prop.value.slice(1)), prop);
        i = propIdx;
        continue;
      }
      // A dot-member can also follow the possessive DIRECTLY (no connector):
      // bn `এর.error` = এর(→its) + source-adjacent `.error` — the plain `.`-glue
      // would leak the untranslatable particle (`if এর.error`). Source adjacency
      // is the same member-access voucher the connector branch above uses (a
      // spaced `.cls` stays a class selector). Additionally gated on the surface
      // NOT doubling as the profile's bare reference: ms `ia` / qu `chay` are
      // both `it` and `its`, and their glued `it.prop` already renders valid —
      // rewriting it would move byte-identical renders for no validity gain.
      // bn's bare `it` is এটি, so এর is unambiguously possessive.
      if (
        !skipsConnector &&
        /^\.[a-zA-Z_]\w*$/.test(next.value) &&
        token.position?.end !== undefined &&
        next.position?.start !== undefined &&
        token.position.end === next.position.start &&
        !isAlsoBareReference(profile, token)
      ) {
        append(getEnglishPossessiveAdjective(reference), token);
        append(translatePropertyName(languageCode, next.value.slice(1)), next);
        i += 1;
        continue;
      }
    }

    // `<property> <of-marker> <selector>` → `value of #price`. Requires the
    // selector to follow, so a real `from`/source clause (which shares the `de`
    // surface in es) is never rewritten. A chain reads the same way, each link
    // the next one's owner, as long as it ends in the selector: es `length de
    // textContent de #a` → `length of textContent of #a` (the renderer's form
    // of `#a's textContent's length`), where only the last link used to move.
    const owner = tokens[i + 2];
    const chainEnd = ofChainEnd(tokens, i, profile, languageCode);
    if (chainEnd !== undefined) {
      for (let k = i; k < chainEnd; k += 2) {
        append(translatePropertyName(languageCode, tokens[k]!.value), tokens[k]!);
        append('of', tokens[k + 1]!);
      }
      append(tokens[chainEnd]!.value, tokens[chainEnd]!);
      i = chainEnd;
      continue;
    }

    // OWNER-FIRST genitive — `<selector> <genitive> <property>` → `value of
    // #price` — the shape every suffix/particle-genitive language renders
    // (ja `#priceの 値`, ko `#price의 값`, zh `#price的 值`, bn `#priceর মান`,
    // qu `#price pa chanin`, tr `#price ın değer`). The branch above reads the
    // prepositional order only, so these leaked verbatim and the canonical
    // parser threw on the particle ("Unknown token: の"). Same marker table,
    // same property lexicon; gated on the property head being one the lexicon
    // knows, so `#modal に 表示` (a dative marker before a verb) is untouched.
    // Where the marker is the profile's between-position genitive, the anchor
    // at the top of the loop reads the pair first, for any property word; this
    // one still reads the rest (qu `#price pa chanin`, tr `#price ın değer`).
    if (
      token.kind === 'selector' &&
      next &&
      owner &&
      isOfPossessiveMarker(profile, next) &&
      isPropertyHeadCandidate(owner, languageCode) &&
      translatePropertyName(languageCode, owner.value) !== owner.value
    ) {
      append(translatePropertyName(languageCode, owner.value), owner);
      append('of', next);
      append(token.value, token);
      i += 2;
      continue;
    }

    // `<positional> <selector> [.member]* [<locative> <selector>]` →
    // `last <button/> in .modal`. The SAME recognizer the positional ROLE capture
    // uses, so the two seams cannot disagree: focus-trap authors that run TWICE —
    // once in the condition, once in the then-branch — and the then-branch (which
    // reaches the recognizer via the pattern matcher) rendered `in` in all 24
    // languages while the condition, joined here, leaked. Two ways, not one:
    // verbatim (ru `в`, zh `在`, tr `içinde` — not role markers at all) and as the
    // ROLE NAME (de/th `in`/`ใน` → `destination`), since role markers are
    // registered with `normalized: <role>`.
    //
    // Deliberately placed AFTER the of-possessive anchor: this way the run can only
    // claim tokens the earlier anchors declined, so no existing rewrite changes. The
    // reverse order would flip `<positional> <sel> <of-marker> <sel>` away from
    // `<sel> of <sel>`, and for a marker absent from LOCATIVE_SURFACES (zh `的`,
    // ja `の`) toEnglishLocative falls through to the verbatim surface — worse than
    // the leak it replaces.
    const run = matchPositionalRun(tokens, i, profile);
    if (run) {
      for (const part of run.parts) append(part.text, part.token);
      i += run.consumed - 1;
      continue;
    }

    // A query's scope, `<query> <in> <scope>`: a translation writes the
    // language's `in` there (es `si ningún <li/> en #list`, M2 sheet A5), and
    // the positional run above reads it only after a positional word.
    const scoped =
      previous?.kind === 'selector' && previous.value.startsWith('<')
        ? matchQueryScope(tokens, i, profile)
        : null;
    if (scoped) {
      append('in', scoped.marker);
      for (const part of scoped.scope.parts) append(part.text, part.token);
      i += scoped.scope.consumed;
      continue;
    }

    // Ambiguous-sense anchor — AFTER the possessive/of/positional anchors (so
    // it can only claim tokens they declined) and before the connective table
    // (these surfaces are deliberately NOT connectives; the sense table is
    // authoritative for them). See AMBIGUOUS_SENSES above for why this seam is
    // the one safe place to translate these words.
    append(expressionWordOf(languageCode, { tokens, i }, previousText, tokens[i + 2]), token);
  }

  // A translation writes `X does not match Y` as the language's `X not matches
  // Y` (es `yo no coincide .x`, value-lexicon.ts): English's phrase, between
  // two operands.
  return out.trim().replace(/(\S) not matches (?=\S)/g, '$1 does not match ');
}
